import React, { useState, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { changeChartSize } from "redux/ducks/Chart"

interface props {
  canvasId: string
  parentElement: string
  heightFlag?: boolean
  minichartFlag?: boolean
  resizeTrigger?: boolean
  effector?: boolean
}

class SizeController {
  width: number
  height: number
  constructor() {
    this.width = 0
    this.height = 0
  }
  setHeight = (height: number) => {
    this.height = height
  }
  setWidth = (width: number) => {
    this.width = width
  }
}

class ResizeTimer {
  func: NodeJS.Timeout
  constructor() {
    this.func = null
  }
}

const MINI_CHART_HEIGHT = 200

const useChartResize = (props: props) => {
  const { canvasId, parentElement, effector } = props
  const heightFlag = useMemo(() => {
    return !!props.heightFlag
  }, [])
  const minichartFlag = !!props.minichartFlag
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const providers = useSelector(providersSelector)
  const [_provider, _controller, _procs, _resetCtx] = useMemo(() => {
    return providers
  }, [providers])
  const sizeController = useMemo(() => {
    return new SizeController()
  }, [])
  const resizeTimer = useMemo(() => {
    return new ResizeTimer()
  }, [])
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(MINI_CHART_HEIGHT)

  const _resize = () => {
    if (!_controller || !sizeController.height) return
    clearTimeout(resizeTimer.func)

    resizeTimer.func = setTimeout(() => {
      const container = document.getElementById(parentElement)
      const _width = Math.ceil(container.clientWidth)
      const _height = Math.ceil(container.clientHeight)
      // width/heightが同じ大きさでサイズ変更しようとするとバグるため、等しい場合は抜ける。
      if (
        (!heightFlag && _width === sizeController.width) ||
        (heightFlag &&
          _width === sizeController.width &&
          _height === sizeController.height)
      )
        return
      setWidth(_width)
      sizeController.setWidth(_width)
      if (heightFlag) {
        sizeController.setHeight(_height)
        setHeight(sizeController.height)
      }
      _controller.SetSize.call(null, [_width, sizeController.height])

      _resetCtx(null)
      // サイズ変更と同時に描画するパターン。ヌルヌル動くが、canvasのrenderより早く処理が走ると、一瞬だけ拡大して見える時がある。
      _controller.Chart.Draw()
    }, 250)
  }

  useEffect(() => {
    if (!_controller) return
    const container = document.getElementById(parentElement)
    const width = Math.ceil(container.clientWidth)
    const _height = Math.ceil(container.clientHeight)
    setWidth(width)
    setHeight(!minichartFlag ? _height : MINI_CHART_HEIGHT)
    _controller.SetSize.call(null, [width, _height])
    sizeController.setHeight(_height)
    sizeController.setWidth(width)
    // 初期値設定する際に、setTimeout入れないと、きれいに描画されない。しかし、resize関数内に入れてしまうと、断続的に見えて目がチカチカするため、useEffect内に別定義。
    setTimeout(() => {
      _resetCtx(null)
      _controller.Chart.Draw()
    })
  }, [_controller])

  useEffect(() => {
    if (!_controller) return
    const container = document.getElementById(parentElement)
    const width = Math.ceil(container.clientWidth)
    const _height = Math.ceil(container.clientHeight)
    setWidth(width)
    setHeight(!minichartFlag ? _height : MINI_CHART_HEIGHT)
    _controller.SetSize.call(null, [width, _height])
    sizeController.setHeight(_height)
    sizeController.setWidth(width)
    // 初期値設定する際に、setTimeout入れないと、きれいに描画されない。しかし、resize関数内に入れてしまうと、断続的に見えて目がチカチカするため、useEffect内に別定義。
    setTimeout(() => {
      _resetCtx(null)
      _controller.Chart.Draw()
    })
  }, [props.resizeTrigger, effector])

  // サイズが変更し終わった後に描画するパターン。挙動は安定するが、動かしている間はチャートが消える。
  // useEffect(() => {
  //   if (!_controller) return
  //   _controller.Chart.Draw()
  // }, [size.width])

  return {
    resize: () => window.addEventListener("resize", _resize, false),
    removeResize: () => window.removeEventListener("resize", _resize, false),
    size: {
      width,
      height,
      scaleWidth: width * 2,
      scaleHeight: height * 2,
    },
  }
}

export default useChartResize
